"use client"
import { useCallback, useEffect, useRef, useState } from "react"

export const useScrollAnchor = <T,>(messages: T, follow: boolean = false) => {
  const messagesRef = useRef<HTMLDivElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const visibilityRef = useRef<HTMLDivElement>(null)
  const bottomRef = useRef<HTMLDivElement>(null)

  const [isAtBottom, setIsAtBottom] = useState(true)
  const [isFollowing, setIsFollowing] = useState(follow)

  const scrollToBottom = useCallback(() => {
    if (visibilityRef.current) {
      setIsFollowing(true)
      visibilityRef.current.scrollIntoView({
        block: "end",
        behavior: "smooth",
      })
    }
  }, [isFollowing, visibilityRef.current])

  useEffect(() => {
    if (scrollRef.current) {
      const onUserScrollUp = (e: any) => {
        const lastKnownScrollY = e.target.dataset.lastKnownScrollY ?? 0
        if (lastKnownScrollY > e.target.scrollTop) {
          setIsFollowing(false)
        }
        e.target.dataset.lastKnownScrollY = e.target.scrollTop
      }

      scrollRef.current.addEventListener("scroll", onUserScrollUp)

      return () => {
        scrollRef.current?.removeEventListener("scroll", onUserScrollUp)
      }
    }
  }, [scrollRef.current])

  useEffect(() => {
    if (isFollowing) {
      scrollToBottom()
    }
  }, [isAtBottom, isFollowing, messages])

  useEffect(() => {
    if (visibilityRef.current) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsAtBottom(true)
            setIsFollowing(true)
          } else {
            setIsAtBottom(false)
          }
        })
      })

      observer.observe(visibilityRef.current)

      return () => {
        observer.disconnect()
      }
    }
  })

  return {
    messagesRef,
    scrollRef,
    visibilityRef,
    bottomRef,
    scrollToBottom,
    isAtBottom,
    isFollowing,
    setIsFollowing,
  }
}
